import ExcelJS from 'exceljs';
import type { BrandingConfig } from '@/types/database/portal-types';
import { createDownloadAnchor } from '@/lib/downloadAnchor';
import {
  brandingHexToExcelArgb,
  brandingMixWithWhite,
} from '@/utils/brandingHexToHsl';

/** Entravia default palette. Kept exported for callers that have no branding context. */
export const EXCEL_BRAND_COLORS = {
  primary: '0B5E3B',      // #0b5e3b - Primary green
  highlight: '0AD485',    // #0ad485 - Highlight green
  primaryLight: 'E8F5EE', // Light green background
  white: 'FFFFFF',
  black: '000000',
  gray: '6B7280',
  lightGray: 'F3F4F6',
};

export type ExcelBrandPalette = typeof EXCEL_BRAND_COLORS;

/**
 * Builds an Excel palette from a portal/broker BrandingConfig, falling back to
 * the Entravia defaults when a field is missing or malformed. `white`, `black`,
 * `gray`, `lightGray` are neutral and always inherited from the defaults.
 */
export function getExcelBrandPalette(
  branding: BrandingConfig | null | undefined,
): ExcelBrandPalette {
  if (!branding) return EXCEL_BRAND_COLORS;
  const primary = branding.primary_color
    ? brandingHexToExcelArgb(branding.primary_color)
    : null;
  const highlight = branding.secondary_color
    ? brandingHexToExcelArgb(branding.secondary_color)
    : null;
  return {
    ...EXCEL_BRAND_COLORS,
    primary: primary ?? EXCEL_BRAND_COLORS.primary,
    highlight: highlight ?? EXCEL_BRAND_COLORS.highlight,
    primaryLight: primary
      ? (brandingMixWithWhite(`#${primary}`, 0.92) ?? EXCEL_BRAND_COLORS.primaryLight)
      : EXCEL_BRAND_COLORS.primaryLight,
  };
}

// Header style with primary brand color
export const getHeaderStyle = (
  palette: ExcelBrandPalette = EXCEL_BRAND_COLORS,
): Partial<ExcelJS.Style> => ({
  font: {
    bold: true,
    color: { argb: palette.white },
    size: 11,
  },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: palette.primary },
  },
  alignment: {
    horizontal: 'center',
    vertical: 'middle',
  },
  border: {
    top: { style: 'thin', color: { argb: palette.primary } },
    bottom: { style: 'thin', color: { argb: palette.primary } },
    left: { style: 'thin', color: { argb: palette.primary } },
    right: { style: 'thin', color: { argb: palette.primary } },
  },
});

// Section header style
export const getSectionHeaderStyle = (
  palette: ExcelBrandPalette = EXCEL_BRAND_COLORS,
): Partial<ExcelJS.Style> => ({
  font: {
    bold: true,
    color: { argb: palette.primary },
    size: 11,
  },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: palette.primaryLight },
  },
  alignment: {
    horizontal: 'left',
    vertical: 'middle',
  },
});

// Title style for main headers
export const getTitleStyle = (
  palette: ExcelBrandPalette = EXCEL_BRAND_COLORS,
): Partial<ExcelJS.Style> => ({
  font: {
    bold: true,
    color: { argb: palette.white },
    size: 16,
  },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: palette.primary },
  },
  alignment: {
    horizontal: 'center',
    vertical: 'middle',
  },
});

// Subtitle style
export const getSubtitleStyle = (
  palette: ExcelBrandPalette = EXCEL_BRAND_COLORS,
): Partial<ExcelJS.Style> => ({
  font: {
    italic: true,
    color: { argb: palette.gray },
    size: 10,
  },
  alignment: {
    horizontal: 'center',
    vertical: 'middle',
  },
});

// Apply header style to a row
export const applyHeaderStyleToRow = (
  row: ExcelJS.Row,
  palette: ExcelBrandPalette = EXCEL_BRAND_COLORS,
) => {
  const style = getHeaderStyle(palette);
  row.eachCell((cell) => {
    Object.assign(cell, { style });
  });
  row.height = 22;
};

// Download workbook as file
export const downloadWorkbook = async (workbook: ExcelJS.Workbook, filename: string): Promise<void> => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  const url = URL.createObjectURL(blob);
  const link = createDownloadAnchor();
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Get workbook as buffer (for ZIP exports)
export const getWorkbookBuffer = async (workbook: ExcelJS.Workbook): Promise<ArrayBuffer> => {
  return await workbook.xlsx.writeBuffer() as ArrayBuffer;
};

// Set column widths
export const setColumnWidths = (worksheet: ExcelJS.Worksheet, widths: number[]) => {
  widths.forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });
};

// Add data to worksheet from array of arrays
export const addDataToWorksheet = (
  worksheet: ExcelJS.Worksheet, 
  data: (string | number | boolean | null)[][],
  startRow: number = 1
) => {
  data.forEach((rowData, index) => {
    const row = worksheet.getRow(startRow + index);
    rowData.forEach((value, colIndex) => {
      row.getCell(colIndex + 1).value = value;
    });
    row.commit();
  });
};
